import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { GitBranch, Check } from 'lucide-react'
import type { RunbookTask } from '@/types/entities'
import { formatMinutesToDHM } from './formatTime'

/**
 * Runbook option groups — the OR decision inside a runbook.
 *
 * A runbook holds every path it might take, because a customer can own a capability (the
 * PXE recovery server, the execution bridge) and still choose not to use it on the day.
 * Tasks sharing an `optionGroup` are mutually exclusive alternatives, `optionName` says
 * which branch a task belongs to, and `optionDefault` marks the planned one.
 *
 * Mirrors backend/src/utils/runbook-options.js. If the two drift the symptom is a group
 * that renders as a decision on screen but is stored ungrouped, so keep them together.
 */

const MAX_LEN = 64

/** Trim, bound and blank-to-null. These are labels, not identifiers — case is preserved. */
export function normaliseLabel(value: string | null | undefined): string | null {
    const s = String(value ?? '').trim().slice(0, MAX_LEN)
    return s === '' ? null : s
}

/** A task is part of a decision only when BOTH the group and the branch name are set. */
export function isOptionTask(task: Partial<RunbookTask> | null | undefined): boolean {
    return !!normaliseLabel(task?.optionGroup) && !!normaliseLabel(task?.optionName)
}

export interface OptionBranch {
    name: string
    tasks: RunbookTask[]
    isDefault: boolean
    /** Sum of the branch's own durations — NOT the RTO delta, which only the critical path decides. */
    duration: number
}

export type RunbookEntry =
    | { type: 'task'; task: RunbookTask; index: number }
    | { type: 'group'; group: string; branches: OptionBranch[]; indices: number[] }

/**
 * Walk tasks in order and fold consecutive same-group tasks into one decision entry,
 * carrying each task's original index so callers can still edit and delete by position.
 *
 * Contiguity is deliberate: a decision draws as a block spanning its branches, so a group
 * split across the runbook renders as two blocks rather than silently reordering someone's
 * tasks to make the drawing work.
 */
export function buildRunbookEntries(tasks: RunbookTask[]): RunbookEntry[] {
    const out: RunbookEntry[] = []
    const list = tasks || []
    let i = 0
    while (i < list.length) {
        const group = normaliseLabel(list[i]?.optionGroup)
        if (!group || !isOptionTask(list[i])) {
            out.push({ type: 'task', task: list[i], index: i })
            i += 1
            continue
        }
        const run: { task: RunbookTask; index: number }[] = []
        while (i < list.length && normaliseLabel(list[i]?.optionGroup) === group && isOptionTask(list[i])) {
            run.push({ task: list[i], index: i })
            i += 1
        }
        const byBranch = new Map<string, { task: RunbookTask; index: number }[]>()
        for (const item of run) {
            const name = normaliseLabel(item.task.optionName) as string
            if (!byBranch.has(name)) byBranch.set(name, [])
            byBranch.get(name)!.push(item)
        }
        out.push({
            type: 'group',
            group,
            indices: run.map((r) => r.index),
            branches: [...byBranch.entries()].map(([name, items]) => ({
                name,
                tasks: items.map((it) => it.task),
                isDefault: items.some((it) => !!it.task.optionDefault),
                duration: items.reduce((sum, it) => sum + (Number(it.task.duration) || 0), 0),
            })),
        })
    }
    return out
}

/** How many real decisions a runbook contains — a group needs 2+ branches to be a choice. */
export function countDecisions(tasks: RunbookTask[]): number {
    return buildRunbookEntries(tasks).filter((e) => e.type === 'group' && e.branches.length > 1).length
}

/**
 * Planned and worst-case runbook duration.
 *
 * Planned follows the default branch of each group; worst takes the slowest branch. Both
 * are branch-sum approximations for display — the authoritative RTO is the critical path
 * computed by calculateRunbookTimes and the SQL views. Shown as a range because with an
 * unresolved decision there is no single number, and pretending otherwise is what makes a
 * published RTO wrong.
 */
export function runbookDurationRange(tasks: RunbookTask[]): { planned: number; worst: number } {
    let planned = 0
    let worst = 0
    for (const entry of buildRunbookEntries(tasks)) {
        if (entry.type === 'task') {
            const d = Number(entry.task?.duration) || 0
            planned += d
            worst += d
            continue
        }
        const chosen = entry.branches.find((b) => b.isDefault) ?? entry.branches[0]
        planned += chosen?.duration ?? 0
        worst += Math.max(...entry.branches.map((b) => b.duration), 0)
    }
    return { planned, worst }
}

/** The decision pill — one colour across every surface, meaning "you must choose". */
export function DecisionBadge({ group, branches, className = '' }: { group: string; branches?: number; className?: string }) {
    const { t } = useTranslation()
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm font-medium border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300 ${className}`}
        >
            <GitBranch className="h-4 w-4 flex-shrink-0" aria-hidden />
            <span className="truncate">{group}</span>
            {branches != null && (
                <span className="opacity-70">
                    · {t('objects.runbook.chooseOne', 'choose one')}
                </span>
            )}
        </span>
    )
}

/** Branch label used in the table's Option column and on the card branch panels. */
export function BranchBadge({
    name,
    isDefault,
    skipped = false,
    className = '',
}: {
    name: string
    isDefault?: boolean
    skipped?: boolean
    className?: string
}) {
    const tone = skipped
        ? 'border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-400'
        : isDefault
            ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300'
            : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300'
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${tone} ${className}`}>
            {isDefault && !skipped && <Check className="h-3 w-3 flex-shrink-0" aria-hidden />}
            <span className="truncate">{name}</span>
        </span>
    )
}

/**
 * The decision block: one bordered container holding every branch of a group, with the
 * planned branch marked and the alternatives dimmed but never hidden.
 *
 * Branches stack rather than sitting side by side as in the design sketch — real task
 * text and the inline editor need the full card width, and two 360px columns made both
 * unusable. The structure the sketch was communicating is intact: one block, a header
 * that says choose one, a branch header carrying its own cost, and the road not taken
 * visible underneath.
 */
export function OptionGroupBlock({
    entry,
    renderTaskCard,
}: {
    entry: Extract<RunbookEntry, { type: 'group' }>
    readOnly?: boolean
    renderTaskCard: (task: RunbookTask, idx: number) => React.ReactNode
}) {
    const { t } = useTranslation()
    const isDecision = entry.branches.length > 1
    const fastest = Math.min(...entry.branches.map((b) => b.duration))

    return (
        <div className="rounded-xl border-2 border-blue-300 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/10">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-blue-200 dark:border-blue-900">
                <DecisionBadge group={entry.group} branches={isDecision ? entry.branches.length : undefined} />
                {isDecision && (
                    <span className="text-xs text-blue-800 dark:text-blue-300">
                        {entry.branches
                            .map((b) => `${b.name} ${formatMinutesToDHM(b.duration)}`)
                            .join(' · ')}
                    </span>
                )}
            </div>

            <div className="p-3 space-y-3">
                {entry.branches.map((branch) => (
                    <div
                        key={branch.name}
                        className={`rounded-lg border bg-background ${branch.isDefault
                            ? 'border-emerald-300 dark:border-emerald-900'
                            : 'border-gray-200 dark:border-gray-700'}`}
                    >
                        <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 border-b bg-muted/40">
                            <BranchBadge name={branch.name} isDefault={branch.isDefault} />
                            <span className="text-xs text-muted-foreground">
                                {branch.tasks.length} {t('objects.runbook.tasks', 'tasks')} · {formatMinutesToDHM(branch.duration)}
                            </span>
                            {branch.isDefault && (
                                <span className="text-xs text-emerald-700 dark:text-emerald-400">
                                    {t('objects.runbook.plannedBranch', 'planned')}
                                </span>
                            )}
                            {isDecision && branch.duration > fastest && (
                                <span className="text-xs text-muted-foreground">
                                    +{formatMinutesToDHM(branch.duration - fastest)}
                                </span>
                            )}
                        </div>
                        <div className={`p-2 space-y-3 ${branch.isDefault || !isDecision ? '' : 'opacity-70'}`}>
                            {branch.tasks.map((task) => renderTaskCard(task, (task as any)._index))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
